import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogActions, Button, IconButton, TextField, MenuItem, Grid, Autocomplete } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { DEPARTMENTS, DESIGNATIONS, optionLabel } from "../../helpers/orgOptions";

/* Only the fields the server accepts on this screen. Email is the login
   identity and the password is never touched from here. */
const blank = {
    employeeName: "", designation: "", department: "", officeLocation: "",
    workLocation: "", managerName: "", managerEmail: "", mobile: "", gender: "",
    experience: "",
};

const GENDERS = ["Male", "Female", "Other"];

const EmployeeEdit = ({ employee, onClose, onSave }) => {
    const [form, setForm] = useState({ ...blank });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!employee) return;
        setForm({
            employeeName: employee.employeeName || "",
            designation: employee.designation || "",
            department: employee.department || "",
            officeLocation: employee.officeLocation || "",
            workLocation: employee.workLocation || "",
            managerName: employee.managerName || "",
            managerEmail: employee.managerEmail || "",
            mobile: employee.mobile || "",
            gender: employee.gender || "",
            experience: employee.experience || "",
        });
    }, [employee]);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async () => {
        if (!form.employeeName.trim()) return;
        setSaving(true);
        await onSave({ ...form, employeeCode: employee.employeeCode });
        setSaving(false);
    };

    return (
        <Dialog open={Boolean(employee)} onClose={onClose} fullWidth maxWidth="md" className="wh-dialog">
            {employee && (
                <>
                    <div className="wh-dialog-head">
                        <div className="wh-dialog-headings">
                            <h5>Edit {employee.employeeName}</h5>
                            <p>{employee.employeeCode} · {employee.emailID}</p>
                        </div>
                        <IconButton className="wh-dialog-close" onClick={onClose} aria-label="Close">
                            <CloseRounded />
                        </IconButton>
                    </div>

                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Employee name"
                                    value={form.employeeName} onChange={set("employeeName")}
                                    error={!form.employeeName.trim()}
                                    helperText={!form.employeeName.trim() ? "Name is required" : " "} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    freeSolo
                                    autoHighlight
                                    size="small"
                                    options={DESIGNATIONS}
                                    groupBy={(option) => option.group}
                                    getOptionLabel={optionLabel}
                                    value={form.designation || ""}
                                    onChange={(e, picked) => setForm((prev) => ({ ...prev, designation: optionLabel(picked) }))}
                                    onInputChange={(e, typed) => setForm((prev) => ({ ...prev, designation: typed }))}
                                    componentsProps={{ paper: { className: "wh-ac-paper" } }}
                                    ListboxProps={{ className: "wh-ac-listbox" }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Designation" helperText=" " />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    freeSolo
                                    autoHighlight
                                    size="small"
                                    options={DEPARTMENTS}
                                    getOptionLabel={optionLabel}
                                    value={form.department || ""}
                                    onChange={(e, picked) => setForm((prev) => ({ ...prev, department: optionLabel(picked) }))}
                                    onInputChange={(e, typed) => setForm((prev) => ({ ...prev, department: typed }))}
                                    componentsProps={{ paper: { className: "wh-ac-paper" } }}
                                    ListboxProps={{ className: "wh-ac-listbox" }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Department" helperText=" " />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Mobile"
                                    value={form.mobile} onChange={set("mobile")} helperText=" " />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Experience"
                                    placeholder="6 Years"
                                    value={form.experience} onChange={set("experience")}
                                    helperText="Total professional experience" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Manager name"
                                    value={form.managerName} onChange={set("managerName")} helperText=" " />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Manager email"
                                    value={form.managerEmail} onChange={set("managerEmail")} helperText=" " />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Office location"
                                    value={form.officeLocation} onChange={set("officeLocation")} helperText=" " />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" label="Work location"
                                    value={form.workLocation} onChange={set("workLocation")} helperText=" " />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField select fullWidth size="small" label="Gender"
                                    value={form.gender} onChange={set("gender")} helperText=" ">
                                    {GENDERS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button variant="contained" color="inherit" onClick={onClose}>Cancel</Button>
                        <Button variant="contained" onClick={submit} disabled={saving || !form.employeeName.trim()}>
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default EmployeeEdit;
